import { useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { formatDistance } from 'date-fns';
import { useFlag } from '@/features/unleash';
import { Sentry } from '@/components/Sentry';
import {
  Grid,
  Text,
  TabList,
  TabPanel,
  Tab,
  useTheme,
} from '@simplywallst/ui-core';
import { Badge, cn } from '@simplywallst/ui-core/next';
import LinkOutline from '@simplywallst/ui-core/icons/LinkOutline';
import LockSolid from '@simplywallst/ui-core/icons/LockSolid';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import BrazeContentCard from '@/components/BrazeContentCard';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { useIsMobileScreen } from '@/hooks/useMedia';

import { PortfolioSettings } from '../PortfolioSettings';
import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { HoldingsPanel } from '../../../../components/HoldingsPanel';
import { AddTransactionButton } from '../../../../components/AddTransaction';

import { StyledHr, BrazeContentCardWrapper } from './styled';
import { TrackLandEvent } from './TrackLandEvent';
import { DividendDefaultError } from '../../../../components/DividendPanel/components/DividendDefaultError';
import { RenderUntilExpiry } from '@/components/RenderUntilExpiry';
import { Footer } from '@/components/Footer';
import { useFlag as useUnleashFlag } from '@simplywallst/unleash';
import { NarrativePanel } from '@/features/portfolio/components/NarrativePanel';
import { PrimaryNarrative } from '@/components/NarrativeTable/components/PrimaryNarrative';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

const RestrictionPlanModal = loadable(
  () => import('../../../../components/RestrictionPlanModal'),
  {
    resolveComponent: (c) => c.RestrictionPlanModal,
  }
);

const AnalysisPanel = loadable(
  () => import('../../../../components/AnalysisPanel'),
  {
    resolveComponent: (c) => c.AnalysisPanel,
  }
);

const DividendPanel = loadable(
  () => import('../../../../components/DividendPanel'),
  {
    resolveComponent: (c) => c.DividendPanel,
  }
);

const EstimatedFuturePayments = loadable(
  () =>
    import(
      '../../../../components/DividendPanel/components/EstimatedFuturePayments'
    ),
  {
    resolveComponent: (c) => c.EstimatedFuturePayments,
  }
);

type PortfolioTab = 'holdings' | 'analysis' | 'dividend' | 'narratives';

type Props = {
  portfolio: Portfolio;
};

export const PopulatedState = ({ portfolio }: Props) => {
  const theme = useTheme();
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [selectedPanel, setSelectedPanel] = useState<PortfolioTab>('holdings');

  const { Track } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });

  // Wombats Flags
  const flag = useFlag({ name: 'mono-portfolio-transactions-table' });

  const hasAccessToPortfolioTransactions = flag.flagsReady && flag.enabled;

  // Dingoes Flags
  const { variant: upgradePopupVariant } = useFlag({
    name: 'mono-portfolio-upgrade-popup',
  });

  // Narwhals Flags
  const narwhalsFlag = useUnleashFlag('mono-narrative-portfolio');

  const shouldShowUpgradePopup =
    upgradePopupVariant.name === 'variant' && portfolio.isAnalysisRestricted;

  const timeSinceLastUpdate = useMemo(() => {
    return formatDistance(new Date(portfolio.updatedAt), new Date(), {
      addSuffix: true,
    });
  }, [portfolio.updatedAt]);

  // NOTE: This is a temporary solution to handle the different types of portfolios
  // until we have a more robust solution for handling different portfolio types
  const isManualTransactionsPortfolio =
    hasAccessToPortfolioTransactions &&
    portfolio.type === 'manual' &&
    portfolio.manualInputOption === 'transactions';

  return (
    <Track>
      <TrackLandEvent portfolio={portfolio} />
      {shouldShowUpgradePopup && (
        <RestrictionPlanModal portfolio={portfolio} limitedType="fully" />
      )}
      <MiniPriceChartDefinitions />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="new-portfolio.top"
          anchorTarget="_blank"
          className="px-x2 lg:px-0"
        />
      </BrazeContentCardWrapper>
      <div className="mt-x2 md:my-x2">
        <PortfolioPageHeader portfolio={portfolio}>
          {isMobileScreen ? (
            <div
              className={cn(
                'grid gap-x1',
                isManualTransactionsPortfolio
                  ? 'grid-cols-[max-content_max-content]'
                  : 'grid-cols-[max-content]'
              )}
            >
              <PortfolioSettings portfolio={portfolio} />
              {isManualTransactionsPortfolio && (
                <AddTransactionButton
                  variant="primary"
                  size="medium"
                  portfolioId={portfolio.id}
                >
                  <PlusSolid /> Transactions
                </AddTransactionButton>
              )}
            </div>
          ) : null}
          <Grid
            templateRows="auto"
            templateColumns="minmax(min-content, max-content) auto"
            templateColumnsMd={`auto max-content ${
              portfolio.type === 'linked' ? ' max-content' : ''
            }`}
            columnGap="20px"
            alignItems="center"
          >
            <Text typography="tiny" color="whiteRGBA" opacity={0.3}>
              Updated{' '}
              <Text as="span" color="whiteRGBA" opacity={0.5}>
                {timeSinceLastUpdate}
              </Text>
            </Text>
            {portfolio.type === 'linked' && (
              <Grid templateColumns="min-content auto" alignItems="center">
                <LinkOutline
                  fill="white"
                  opacity={0.3}
                  width="24px"
                  height="24px"
                />
                <Text typography="tiny" color="whiteRGBA" opacity={0.3}>
                  Linked with {portfolio.link.brokerageName}
                </Text>
              </Grid>
            )}
            {!isMobileScreen ? (
              <div
                className={cn(
                  'grid gap-x1',
                  isManualTransactionsPortfolio
                    ? 'grid-cols-[max-content_max-content]'
                    : 'grid-cols-[max-content]'
                )}
              >
                <PortfolioSettings portfolio={portfolio} />
                {isManualTransactionsPortfolio && (
                  <AddTransactionButton
                    variant="primary"
                    size="medium"
                    portfolioId={portfolio.id}
                  >
                    <PlusSolid /> Add transaction
                  </AddTransactionButton>
                )}
              </div>
            ) : null}
          </Grid>
        </PortfolioPageHeader>
        <Grid>
          <TabList templateColumns="repeat(4, max-content)">
            <Tab<PortfolioTab>
              value="holdings"
              selected={selectedPanel === 'holdings'}
              onClick={() => setSelectedPanel('holdings')}
              styleType="subnav"
            >
              Holdings
            </Tab>
            {narwhalsFlag.enabled && narwhalsFlag.ready ? (
              <Tab<PortfolioTab>
                value="narratives"
                selected={selectedPanel === 'narratives'}
                onClick={() => setSelectedPanel('narratives')}
                styleType="subnav"
              >
                Narratives
              </Tab>
            ) : null}
            <Tab<PortfolioTab>
              value="analysis"
              selected={selectedPanel === 'analysis'}
              disabled={portfolio.isAnalysisRestricted}
              onClick={() => setSelectedPanel('analysis')}
              styleType="subnav"
            >
              <Grid templateColumns="auto auto" alignItems="center">
                {portfolio.isAnalysisRestricted ? (
                  <LockSolid
                    aria-label="Blocked content"
                    fill={theme.color.attentionStrong}
                  />
                ) : null}
                Analysis
              </Grid>
            </Tab>
            <Tab<PortfolioTab>
              value="dividend"
              selected={selectedPanel === 'dividend'}
              disabled={portfolio.isAnalysisRestricted}
              onClick={() => setSelectedPanel('dividend')}
              styleType="subnav"
            >
              <Grid
                templateColumns="auto auto"
                alignItems="center"
                gap={portfolio.isAnalysisRestricted ? undefined : '5px'}
              >
                {portfolio.isAnalysisRestricted ? (
                  <LockSolid
                    aria-label="Blocked dividend"
                    fill={theme.color.attentionStrong}
                  />
                ) : null}
                Dividends
                {!portfolio.isAnalysisRestricted && (
                  <RenderUntilExpiry
                    startedFrom="2024-08-07"
                    expiresIn={{ months: 1 }}
                  >
                    <Badge tone="attention" size="small">
                      New
                    </Badge>
                  </RenderUntilExpiry>
                )}
              </Grid>
            </Tab>
          </TabList>
          <StyledHr />
        </Grid>
      </div>
      <TabPanel<PortfolioTab>
        value="holdings"
        selected={selectedPanel === 'holdings'}
      >
        <HoldingsPanel portfolio={portfolio} />
      </TabPanel>
      {narwhalsFlag.enabled && narwhalsFlag.ready ? (
        <TabPanel<PortfolioTab>
          value="narratives"
          selected={selectedPanel === 'narratives'}
        >
          <NarrativePanel
            portfolio={portfolio}
            primaryNarrative={({ item }) => (
              <PrimaryNarrative
                companyId={item.company.id}
                companyName={item.company.name}
                currencyISO={
                  getTradingItemCurrencyISO(item.company.data.currencyInfo) ??
                  'XXX'
                }
                myValuation={item.company.myValuation}
                valuationOptions={item.company.valuationOptions}
                tickerSymbol={item.company.tickerSymbol}
              />
            )}
          />
        </TabPanel>
      ) : null}
      <TabPanel<PortfolioTab>
        value="analysis"
        selected={selectedPanel === 'analysis'}
      >
        <AnalysisPanel portfolio={portfolio} />
      </TabPanel>
      <TabPanel<PortfolioTab>
        value="dividend"
        selected={selectedPanel === 'dividend'}
      >
        <Sentry
          sentryCapture={{
            team: 'dingoes',
          }}
          customCopy={<DividendDefaultError />}
          globalDisabled
          isShownLogo={false}
          isShownHeading={false}
        >
          <DividendPanel
            portfolio={portfolio}
            dividendSections={<EstimatedFuturePayments portfolio={portfolio} />}
          />
        </Sentry>
      </TabPanel>
      <Footer />
    </Track>
  );
};
